import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import WishlistButton from "../Wishlist/WishlistButton";
import { useProductTranslation } from "../../i18n/useProductTranslation";

function formatMoney(value, currency = "EUR") {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency,
  }).format(num);
}

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const { t, getTranslatedProduct } = useProductTranslation();

  const translatedProduct = getTranslatedProduct(product);

  const variants = useMemo(() => {
    if (!Array.isArray(translatedProduct?.variants)) return [];
    return [...translatedProduct.variants]
      .filter((v) => v.is_active !== false)
      .sort((a, b) => Number(a.is_next) - Number(b.is_next));
  }, [translatedProduct?.variants]);

  const hasVariants = variants.length > 0;
  const hasMultipleVariants = variants.length > 1;
  const singleVariant = variants.length === 1 ? variants[0] : null;

  const price = translatedProduct?.display_price ?? translatedProduct?.base_price ?? null;

  return (
    <div
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
      onClick={() => navigate(`/products/${translatedProduct.id}`)}
    >
      <div
        className="relative mb-4 overflow-hidden rounded-xl p-[1px]"
        style={{ background: 'linear-gradient(135deg, #e5e7eb, #f3f4f6)' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Div interior — rounded ligeiramente menor (11px = 12px - 1px de padding) */}
        <div className="overflow-hidden rounded-[11px] bg-white">
          <img
            src={
              hovered && translatedProduct.images?.length > 1
                ? translatedProduct.images[1].image_url
                : translatedProduct.images?.length > 0
                  ? translatedProduct.images[0].image_url
                  : translatedProduct.image_url || "/placeholder-product.png"
            }
            alt={translatedProduct.images?.[0]?.alt_text || translatedProduct.name}
            className="h-48 w-full object-cover transition-all duration-500 group-hover:scale-105"
          />
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-start justify-between gap-2">
          <h2 className="line-clamp-2 text-lg font-semibold text-gray-900">
            {translatedProduct.name}
          </h2>
          <div
            className="shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <WishlistButton
              productId={translatedProduct.id}
              initialFavorite={translatedProduct.is_favorite || false}
            />
          </div>
        </div>

        {translatedProduct.sku && (
          <p className="mt-1 text-sm text-gray-500">{translatedProduct.sku}</p>
        )}
      </div>

      {
        hasVariants ? (
          <div className="mb-4">
            {hasMultipleVariants ? (
              <>
                <p className="text-sm text-gray-500">
                  {t(`${variants.length} variantes disponíveis`)}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">
                  {t("A partir de")}{" "}
                  {formatMoney(
                    Math.min(...variants.map((v) => Number(v.price))),
                    variants[0]?.currency || "EUR"
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  {t("IVA incluído")}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500">{t("1 variante disponível")}</p>
                <p className="mt-0.5 text-xl font-bold text-gray-900">
                  {formatMoney(singleVariant.price, singleVariant.currency || "EUR")}
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="mb-4">
            {translatedProduct.has_price_range && (
              <p className="text-sm text-gray-500">{t("A partir de")}</p>
            )}
            <p className="text-2xl font-bold text-gray-900">
              {price !== null ? formatMoney(price, "EUR") : t("Sob consulta")}
            </p>
            <p className="text-xs text-gray-500">
              {t("IVA incluído")}
            </p>
          </div>
        )
      }

      <div onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => navigate(`/products/${translatedProduct.id}`)}
          className="w-full rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-gray-800"
        >
          {t("Ver detalhes")}
        </button>
      </div>
    </div >
  );
}